import React from "react";
import { Link } from "react-router-dom";
import { useLang } from "../../lib/i18n";
import { useSiteContent, pickLang } from "../../lib/siteContent";
import { Instagram, Linkedin, Mail, MapPin, Phone } from "lucide-react";

const MARQUEE_ITEMS = [
  "Let's stay inTouch",
  "Keep in Touch",
  "Always inTouch",
  "Where science meets warmth",
  "Stay Connected · Stay inTouch",
  "inTouch · İstanbul",
];

function MarqueeBand() {
  // Listeyi iki kez tekrarlıyoruz — sonsuz döngü efekti için
  const items = [...MARQUEE_ITEMS, ...MARQUEE_ITEMS];
  return (
    <div className="w-full overflow-hidden bg-terracotta-400 py-4 select-none" aria-hidden>
      <div className="marquee-track flex items-center gap-0 whitespace-nowrap">
        {items.map((text, i) => (
          <span key={i} className="inline-flex items-center gap-6 px-8">
            <span className="font-serif italic text-cream-100 text-xl md:text-2xl tracking-tight">
              {text}
            </span>
            <span className="text-cream-100/50 text-lg">·</span>
          </span>
        ))}
      </div>
      <style>{`
        .marquee-track {
          animation: marquee-scroll 32s linear infinite;
          width: max-content;
        }
        .marquee-track:hover {
          animation-play-state: paused;
        }
        @keyframes marquee-scroll {
          from { transform: translateX(0); }
          to   { transform: translateX(-50%); }
        }
      `}</style>
    </div>
  );
}

export default function Footer() {
  const { t, lang } = useLang();
  const { content } = useSiteContent();
  const contact = content?.contact || {};
  const address = pickLang(contact.address, lang, "Bağdat Cad. No: 142, Kadıköy\nİstanbul, Türkiye");
  const phone = contact.phone || null;
  const email = contact.email || "hello@intouch.consulting";
  const instagram = contact.instagramUrl || "#";
  const linkedin = contact.linkedinUrl || "#";

  return (
    <>
      <MarqueeBand />
      <footer data-testid="site-footer" className="bg-sage-800 text-cream-100 relative overflow-hidden">
      <div className="container-x pt-20 pb-10 relative z-10">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-10">
          <div className="lg:col-span-5">
            <div className="flex items-baseline gap-1">
              <span className="font-serif text-4xl leading-none">in</span>
              <span className="font-serif italic text-4xl leading-none text-terracotta-400">Touch</span>
            </div>
            <p className="mt-6 max-w-md text-cream-100/80 font-light text-base leading-relaxed">
              {t.footer.tagline}
            </p>
            <div className="flex items-center gap-3 mt-8">
              <a data-testid="social-instagram" href={instagram} target="_blank" rel="noreferrer" aria-label="Instagram" className="w-10 h-10 rounded-full border border-cream-100/20 flex items-center justify-center hover:bg-cream-100 hover:text-sage-800 transition-all">
                <Instagram className="w-4 h-4" strokeWidth={1.5} />
              </a>
              <a data-testid="social-linkedin" href={linkedin} target="_blank" rel="noreferrer" aria-label="LinkedIn" className="w-10 h-10 rounded-full border border-cream-100/20 flex items-center justify-center hover:bg-cream-100 hover:text-sage-800 transition-all">
                <Linkedin className="w-4 h-4" strokeWidth={1.5} />
              </a>
            </div>
          </div>

          <div className="lg:col-span-3">
            <h4 className="text-xs uppercase tracking-[0.24em] text-cream-100/60 font-sans font-medium mb-5">
              {t.footer.quickLinks}
            </h4>
            <ul className="flex flex-col gap-3 text-cream-100/85 font-light">
              <li><Link to="/" className="hover:text-terracotta-400 transition-colors">{t.nav.home}</Link></li>
              <li><Link to="/services" className="hover:text-terracotta-400 transition-colors">{t.nav.services}</Link></li>
              <li><Link to="/about" className="hover:text-terracotta-400 transition-colors">{t.nav.about}</Link></li>
              <li><Link to="/events" className="hover:text-terracotta-400 transition-colors">{t.nav.events}</Link></li>
              <li><Link to="/blog" className="hover:text-terracotta-400 transition-colors">{t.nav.blog}</Link></li>
            </ul>
          </div>

          <div className="lg:col-span-4">
            <h4 className="text-xs uppercase tracking-[0.24em] text-cream-100/60 font-sans font-medium mb-5">
              {t.footer.contact}
            </h4>
            <ul className="flex flex-col gap-4 text-cream-100/85 font-light">
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 mt-1 text-terracotta-400 shrink-0" strokeWidth={1.5} />
                <span className="whitespace-pre-line">{address}</span>
              </li>
              {phone && (
              <li className="flex items-center gap-3">
                <Phone className="w-4 h-4 text-terracotta-400 shrink-0" strokeWidth={1.5} />
                <a href={`tel:${(phone || "").replace(/\s+/g, "")}`} className="hover:text-terracotta-400 transition-colors">{phone}</a>
              </li>
              )}
              <li className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-terracotta-400 shrink-0" strokeWidth={1.5} />
                <a href={`mailto:${email}`} className="hover:text-terracotta-400 transition-colors">{email}</a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-20 pt-6 border-t border-cream-100/10 flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-xs text-cream-100/55 font-light">
          <span>{t.footer.copy}</span>
          <span className="font-serif italic">Built with care · İstanbul</span>
        </div>
      </div>

      {/* Massive brand wordmark */}
      <div aria-hidden className="pointer-events-none select-none -mb-10 md:-mb-20">
        <div className="font-serif text-[22vw] leading-[0.85] text-cream-100/[0.06] text-center tracking-tight">
          in<span className="italic">Touch</span>
        </div>
      </div>
    </footer>
    </>
  );
}
